import { lazy, Suspense } from "react"
import type { RouteRecord } from "vite-react-ssg"
import AppRoot from "@/app/AppRoot"
import PublicLayout from "@/components/layout/PublicLayout"
import RequireAuth from "@/features/auth/RequireAuth"
import RequireRole from "@/features/auth/RequireRole"

import HomePage from "@/routes/home/HomePage"
import ProductosPage from "@/routes/productos/ProductosPage"
import ProductoDetailPage from "@/routes/productos/ProductoDetailPage"
import TratamientosPage from "@/routes/tratamientos/TratamientosPage"
import TratamientoDetailPage from "@/routes/tratamientos/TratamientoDetailPage"
import ServiciosPage from "@/routes/servicios/ServiciosPage"
import ServicioDetailPage from "@/routes/servicios/ServicioDetailPage"
import { CondicionesPage } from "@/routes/static/CondicionesPage"
import { PrivacidadPage } from "@/routes/static/PrivacidadPage"
import { ContactoPage } from "@/routes/static/ContactoPage"
import { NosotrosPage } from "@/routes/static/NosotrosPage"
import NotFoundPage from "@/routes/NotFoundPage"
import { Navigate } from "react-router-dom"

// admin/ y checkout/ quedan fuera del SSG (ver includedRoutes en vite.config.ts): son
// shells hidratados en cliente, así que se cargan con lazy() para no engordar el bundle
// que descargan las páginas de contenido pre-renderizadas (home, productos, tratamientos...).
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"))
const LoginPage = lazy(() => import("@/routes/admin/LoginPage"))
const VentasPage = lazy(() => import("@/routes/admin/VentasPage"))
const VentaDetallePage = lazy(() => import("@/routes/admin/VentaDetallePage"))
const ClientesPage = lazy(() => import("@/routes/admin/ClientesPage"))
const ClienteDetallePage = lazy(() => import("@/routes/admin/ClienteDetallePage"))
const MensajesPage = lazy(() => import("@/routes/admin/MensajesPage"))
const MensajeDetallePage = lazy(() => import("@/routes/admin/MensajeDetallePage"))
const ProductosAdminPage = lazy(() => import("@/routes/admin/ProductosAdminPage"))
const PromocionesPage = lazy(() => import("@/routes/admin/PromocionesPage"))
const CheckoutPage = lazy(() => import("@/routes/checkout/CheckoutPage"))
const CheckoutDatosPage = lazy(() => import("@/routes/checkout/CheckoutDatosPage"))
const CheckoutPagoPage = lazy(() => import("@/routes/checkout/CheckoutPagoPage"))
const CompraExitosaPage = lazy(() => import("@/routes/checkout/CompraExitosaPage"))

function LazyPage({ children }: { children: React.ReactNode }): React.JSX.Element {
	return <Suspense fallback={null}>{children}</Suspense>
}

export const routes: RouteRecord[] = [
	{
		path: "/",
		element: <AppRoot />,
		children: [
			{
				element: <PublicLayout />,
				children: [
					{ index: true, element: <HomePage /> },
					{ path: "productos", element: <ProductosPage /> },
					{ path: "productos/:slug", element: <ProductoDetailPage /> },
					{ path: "tratamientos", element: <TratamientosPage /> },
					{ path: "tratamientos/:slug", element: <TratamientoDetailPage /> },
					{ path: "servicios", element: <ServiciosPage /> },
					{ path: "servicios/:slug", element: <ServicioDetailPage /> },
					{
						path: "checkout",
						element: (
							<LazyPage>
								<CheckoutPage />
							</LazyPage>
						)
					},
					{
						path: "checkout/datos",
						element: (
							<LazyPage>
								<CheckoutDatosPage />
							</LazyPage>
						)
					},
					{
						path: "checkout/pago/:orderId",
						element: (
							<LazyPage>
								<CheckoutPagoPage />
							</LazyPage>
						)
					},
					{
						path: "checkout/compra-exitosa/:id",
						element: (
							<LazyPage>
								<CompraExitosaPage />
							</LazyPage>
						)
					},
					{ path: "nosotros", element: <NosotrosPage /> },
					{ path: "contacto", element: <ContactoPage /> },
					{ path: "condiciones", element: <CondicionesPage /> },
					{ path: "privacidad", element: <PrivacidadPage /> }
				]
			},
			{
				path: "admin/login",
				element: (
					<LazyPage>
						<LoginPage />
					</LazyPage>
				)
			},
			{
				path: "admin",
				element: <RequireAuth />,
				children: [
					{
						element: (
							<LazyPage>
								<AdminLayout />
							</LazyPage>
						),
						children: [
							{ index: true, element: <Navigate to="ventas" replace /> },
							{ path: "ventas", element: <VentasPage /> },
							{ path: "ventas/:id", element: <VentaDetallePage /> },
							{ path: "clientes", element: <ClientesPage /> },
							{ path: "clientes/:id", element: <ClienteDetallePage /> },
							{ path: "mensajes", element: <MensajesPage /> },
							{ path: "mensajes/:id", element: <MensajeDetallePage /> },
							{ path: "productos", element: <ProductosAdminPage /> },
							{
								element: <RequireRole role="admin" />,
								children: [{ path: "promociones", element: <PromocionesPage /> }]
							}
						]
					}
				]
			},
			{ path: "*", element: <NotFoundPage /> }
		]
	}
]
